import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

const SharedHero = ({ pageType, searchQuery = '', setSearchQuery, onSearchSubmit, children }) => {
  const navigate = useNavigate();
  const [localQuery, setLocalQuery] = useState(searchQuery);

  // Sync state if searchQuery changes externally
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  const getPlaceholder = () => {
    switch (pageType) {
      case 'jobs':
      case 'jobs-detail':
        return 'Search jobs (e.g. SSC, Railway, Police, Bank)...';
      case 'admit-cards':
      case 'admit-cards-detail':
        return 'Search admit cards & hall tickets...';
      case 'results':
      case 'results-detail':
        return 'Search results, merit lists & cutoffs...';
      case 'answer-keys':
      case 'answer-keys-detail':
        return 'Search answer keys & objections...';
      case 'scholarships':
      case 'scholarships-detail':
        return 'Search scholarships & welfare schemes...';
      case 'exams-calendar':
      case 'exams-calendar-detail':
        return 'Search exam dates & calendars...';
      case 'home':
      default:
        return 'Search for jobs, admit cards, results, answer keys...';
    }
  };

  const handleFormSubmit = (e) => {
    if (e) e.preventDefault();

    if (onSearchSubmit) {
      onSearchSubmit(e);
      return;
    }

    if (setSearchQuery) {
      setSearchQuery(localQuery);
    }

    const isDetailPage = pageType.endsWith('-detail');
    const baseType = pageType.replace('-detail', '');

    if (isDetailPage || pageType === 'home') {
      let redirectPath = '/';
      if (baseType === 'jobs') redirectPath = '/jobs';
      else if (baseType === 'admit-cards') redirectPath = '/admit-cards';
      else if (baseType === 'results') redirectPath = '/results';
      else if (baseType === 'answer-keys') redirectPath = '/answer-key';
      else if (baseType === 'scholarships') redirectPath = '/scholarships';
      else if (baseType === 'exams-calendar') redirectPath = '/exams-calendar';

      navigate(`${redirectPath}?q=${encodeURIComponent(localQuery)}`);
    }
  };

  const handleClear = () => {
    setLocalQuery('');
    if (setSearchQuery) {
      setSearchQuery('');
    }
  };

  return (
    <div className="w-full hero-banner-container">
      {/* Hero Banner Section - Modern Executive Dark Mesh */}
      <div className="bg-gradient-to-r from-[#070e24] via-[#0f214e] to-[#0a183d] py-4 md:py-5 relative overflow-hidden border-b border-blue-500/25 shadow-xs">
        
        {/* Subtle Ambient Radial Glows */}
        <div 
          className="absolute -top-[70px] left-1/2 -translate-x-1/2 w-[700px] h-[180px] pointer-events-none opacity-60"
          style={{
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.35) 0%, rgba(15, 33, 78, 0) 70%)'
          }}
        />
        <div 
          className="absolute -bottom-[50px] right-[10%] w-[350px] h-[120px] pointer-events-none opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.25) 0%, rgba(15, 33, 78, 0) 70%)'
          }}
        />

        {/* Ambient Grid Overlay */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage: `radial-gradient(#ffffff 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }}
        />

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-5xl mx-auto space-y-2 md:space-y-2.5">
            
            {/* Primary Heading with Highlighted 'Government Exam' */}
            <h1 className="text-[16px] sm:text-[19px] md:text-[22px] lg:text-[23.5px] font-extrabold tracking-tight text-white mb-1.5 leading-tight drop-shadow-sm">
              Sarkari Adda 24x7 – All Your{' '}
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-300 bg-clip-text text-transparent font-black drop-shadow-xs">
                Government Exam
              </span>{' '}
              Updates in One Place
            </h1>

            {/* Subheading - Single Horizontal Line */}
            <p className="text-[11px] sm:text-[12px] md:text-[12.5px] lg:text-[13px] text-slate-300/95 max-w-6xl mx-auto leading-normal font-normal md:whitespace-nowrap">
              Real-time notifications for Sarkari Naukri, Admit Cards, Answer Keys, Results, Exam Dates & Welfare Schemes verified directly from official gazette bulletins.
            </p>

            {/* Trust & Key Features Badges Strip */}
            <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 pt-1">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-[10.5px] sm:text-[11px] text-slate-200 backdrop-blur-xs transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                100% Gazette Verified
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-[10.5px] sm:text-[11px] text-slate-200 backdrop-blur-xs transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                Central & State Notifications
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-[10.5px] sm:text-[11px] text-slate-200 backdrop-blur-xs transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                Live Admit Cards & Keys
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-[10.5px] sm:text-[11px] text-slate-200 backdrop-blur-xs transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                100% Free For All Aspirants
              </span>
            </div>

          </div>
        </div>
      </div>

      {/* Search Bar Section */}
      <div className="bg-gov-paper border-b border-gov-line py-2 md:py-2.5">
        <div className="w-full px-4">
          <div className="flex justify-center">
            <div className="w-full max-w-[460px]">
              <div className="relative">
                <form
                  onSubmit={handleFormSubmit}
                  className="relative group"
                  toolname="search_government_updates"
                  tooldescription="Search Sarkari Adda 24x7 for government jobs, admit cards, results, answer keys, scholarships, and exam dates"
                  role="search"
                >
                  <input
                    type="text"
                    name="query"
                    aria-label="Search government updates"
                    toolparamdescription="Job title, exam name, organisation, qualification, or notification keyword"
                    value={localQuery}
                    onChange={(e) => {
                      setLocalQuery(e.target.value);
                      if (setSearchQuery && !pageType.endsWith('-detail')) {
                        setSearchQuery(e.target.value);
                      }
                    }}
                    placeholder={getPlaceholder()}
                    className="sa-input h-[38px] md:h-[38px] pl-4 pr-20 text-[12px] md:text-[12.5px] shadow-2xs rounded-xl border-slate-300/90 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 transition-all bg-white"
                  />
                  {localQuery && (
                    <button
                      type="button"
                      onClick={handleClear}
                      aria-label="Clear search"
                      className="absolute right-11 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="submit"
                    aria-label="Search"
                    title="Search"
                    className="absolute inset-y-0 right-0 w-11 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white rounded-r-xl transition-all duration-150 flex items-center justify-center cursor-pointer shadow-xs active:scale-95 group-hover:shadow-blue-500/20"
                  >
                    <Search className="w-4 h-4 text-amber-300" />
                  </button>
                </form>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedHero;
